import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import { useDebounce } from 'usehooks-ts'

import MedicalServiceIcon from '@icons/MedicalServiceIcon'

import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import useLoadingState from '@shared/hooks/src/useLoadingState'
import { getTestId } from '@shared/utils'

import usePharmacies from '@hooks/usePharmacies'
import Avatar from '@components/Avatar'

/** Wrapper around MUI Autocomplete to find and select pharmacy */
export default function SelectPharmacy({
  value: pharmacy,
  onChange,
  label,
  error,
  helperText,
  placeholder = 'Search',
  loading: outsideLoading = false,
  inputProps = {},
  queryProps = {},
  ...rest
}) {
  const testId = getTestId(rest, 'pharmacy-selector')

  const [interacted, setInteracted] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const search = useDebounce(inputValue.trim(), 300)

  const { data, isPending, isRefreshing } = usePharmacies(
    { name: search, limit: 50, active: true, ...queryProps },
    {
      // Increase stale time to avoid unnecessary requests
      staleTime: 60 * 1000,
      placeholderData: keepPreviousData,
      enabled: interacted,
    }
  )

  const loading = useLoadingState((interacted && (isPending || isRefreshing)) || outsideLoading)

  return (
    <Autocomplete
      autoComplete
      selectOnFocus
      disableCloseOnSelect={rest.multiple}
      onFocus={() => {
        if (!interacted) setInteracted(true)
      }}
      forcePopupIcon={false}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x}
      value={pharmacy}
      options={data || []}
      loading={loading}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      onChange={(event, pharmacy) => onChange(pharmacy)}
      onInputChange={(event, search) => {
        if (!event || event.type === 'click') return setInputValue('')
        setInputValue(search)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          error={error}
          placeholder={placeholder}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              ...(!rest.multiple && {
                startAdornment: (
                  <Avatar.Icon size="xs" variant="rounded">
                    <MedicalServiceIcon style={{ fontSize: 16 }} />
                  </Avatar.Icon>
                ),
              }),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },

            htmlInput: { ...params.slotProps.htmlInput, 'data-testid': `${testId}-input` },
          }}
          {...inputProps}
        />
      )}
      renderOption={({ key, ...props }, pharmacy, { selected }) => (
        <li key={pharmacy.id} data-testid={`${testId}-${pharmacy.name}`} {...props}>
          {rest.multiple && <Checkbox sx={{ mr: 1 }} checked={selected} />}
          <Box>
            <Typography>{pharmacy.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {[pharmacy.avita && '(Avita)', [pharmacy.city, pharmacy.state, pharmacy.zip].filter(Boolean).join(', ')]
                .filter(Boolean)
                .join(' ')}
            </Typography>
          </Box>
        </li>
      )}
      {...rest}
    />
  )
}
